import { http, HttpResponse, delay } from 'msw';

import {
  allMdmMockAssets,
  allMdmMockEmployees,
  buildDeviceMetricsResponse,
  buildMdmMetricsResponse,
  EXEMPT_FAILS_ASSET_ID,
  EXEMPT_FAILS_EMPLOYEE_ID,
  exemptMockAsset,
  exemptMockEmployee,
  getMdmMockScenario,
  getNoDeviceMockEmployees,
  SEND_FAILS_EMPLOYEE_ID,
  unexemptMockAsset,
} from '@/mocks/data/mdmEnrollmentData';
import { mockMac } from '@/mocks/data/assetData';
import { mockOrganizationEmployees } from '@/mocks/handlers/employeeHandlers';

// Backs the dashboard "Unmanaged devices and employees" tile and its Resolve
// modal. Scenarios and the deliberately failing ids are described in
// mdmEnrollmentData.ts. This file is registered before employeeHandlers, so
// handlers here that only cover part of a route return nothing for the rest
// and the request falls through to the generic handler.

// Reads a JSON body without consuming the request, so a bodiless or non-JSON
// request can still fall through to the generic handler instead of erroring.
const readJson = async <T>(request: Request): Promise<T | undefined> => {
  try {
    return (await request.clone().json()) as T;
  } catch {
    return undefined;
  }
};

const serverError = () =>
  HttpResponse.json({ detail: 'Mock server error' }, { status: 500 });

// "loading" never answers (until the 60s delay is up), so the skeleton stays put
const waitForScenario = async () => {
  const scenario = getMdmMockScenario();
  if (scenario === 'loading') {
    await delay(60_000);
  } else {
    await delay();
  }
  return scenario;
};

export const mdmEnrollmentHandlers = [
  http.get(
    '*/v1/organizations/:organizationId/devices/mdm-metrics',
    async () => {
      const scenario = await waitForScenario();
      if (scenario === 'error') {
        return serverError();
      }
      return HttpResponse.json(buildMdmMetricsResponse(scenario), {
        status: 200,
      });
    }
  ),

  http.get('*/v1/organizations/:organizationId/devices/metrics', async () => {
    const scenario = await waitForScenario();
    if (scenario === 'error') {
      return serverError();
    }
    return HttpResponse.json(buildDeviceMetricsResponse(scenario), {
      status: 200,
    });
  }),

  // Only the exact lookup the tile and modal make (active employees who have
  // no device and aren't exempt) is answered here; every other employee
  // listing, including other has_devices=false views, uses the generic handler.
  http.get(
    '*/v1/organizations/:organizationId/employees',
    async ({ request }) => {
      const url = new URL(request.url);
      const isNoDeviceLookup =
        url.searchParams.get('has_devices') === 'false' &&
        url.searchParams.get('device_not_required') === 'false' &&
        url.searchParams
          .getAll('status')
          .some(status => status.toUpperCase() === 'ACTIVE');
      if (!isNoDeviceLookup) {
        return;
      }

      const scenario = await waitForScenario();
      if (scenario === 'error') {
        return serverError();
      }

      const limit = url.searchParams.get('limit');
      const offset = Number(url.searchParams.get('offset') ?? 0);
      const all = getNoDeviceMockEmployees(scenario);
      const results =
        limit === null ? all : all.slice(offset, offset + Number(limit));
      return HttpResponse.json({ results, total: all.length }, { status: 200 });
    }
  ),

  // The tile resolves device owners through the bulk employee lookup. Answers
  // from the MDM fixtures plus the shared org employees so ids from either
  // source can be requested together.
  http.post(
    '*/v1/organizations/:organizationId/employees/bulk',
    async ({ request }) => {
      const { employee_ids } = (await request.json()) as {
        employee_ids: string[];
      };
      const known = [...allMdmMockEmployees, ...mockOrganizationEmployees];
      const employees_mapping = Object.fromEntries(
        known
          .filter(employee => employee_ids.includes(employee.id))
          .map(employee => [employee.id, employee])
      );
      await delay();
      return HttpResponse.json({ employees_mapping }, { status: 200 });
    }
  ),

  http.post(
    '*/v1/organizations/:organizationId/assets/bulk',
    async ({ request }) => {
      const { asset_ids } = (await request.json()) as { asset_ids: string[] };
      const assets_mapping = Object.fromEntries(
        allMdmMockAssets
          .filter(asset => asset_ids.includes(asset.id))
          .map(asset => [asset.id, asset])
      );
      await delay();
      return HttpResponse.json({ assets_mapping }, { status: 200 });
    }
  ),

  // The device details page, reached from the device links in the tile. Only
  // the MDM fixtures are answered; the rest of the page's data uses the shared
  // mock asset so it has everything it needs to render.
  http.get(
    '*/v1/organizations/:organizationId/assets/:assetId',
    async ({ params }) => {
      const asset = allMdmMockAssets.find(
        ({ id }) => id === String(params.assetId)
      );
      if (!asset) {
        return;
      }
      await delay();
      return HttpResponse.json(
        {
          ...mockMac,
          id: asset.id,
          name: asset.name,
          serialNumber: asset.serial_number,
          assignedTo: asset.assigned_to,
          assignedToEmployee: {
            id: asset.assigned_to_employee.id,
            firstName: asset.assigned_to_employee.first_name,
            lastName: asset.assigned_to_employee.last_name,
          },
          device: {
            ...mockMac.device,
            id: asset.device.id,
            osType: asset.device.os_type,
          },
          mdmStatusOverride: asset.mdm_status_override ?? null,
        },
        { status: 200 }
      );
    }
  ),

  // "Mark not applicable" (and undoing it) on a device
  http.patch(
    '*/v1/organizations/:organizationId/assets/:assetId',
    async ({ request, params }) => {
      const assetId = String(params.assetId);
      if (!allMdmMockAssets.some(asset => asset.id === assetId)) {
        return;
      }

      const body = await readJson<{ mdm_status_override?: string | null }>(
        request
      );
      if (body === undefined) {
        return;
      }
      await delay();
      if (assetId === EXEMPT_FAILS_ASSET_ID) {
        return serverError();
      }
      if (body.mdm_status_override) {
        exemptMockAsset(assetId);
      } else {
        unexemptMockAsset(assetId);
      }
      return new HttpResponse(null, { status: 200 });
    }
  ),

  // "Mark not applicable" on an employee with no device. Other employee edits
  // (no device_not_required in the body) use the generic handler.
  http.patch(
    '*/v1/organizations/:organizationId/employees/:employeeId',
    async ({ request, params }) => {
      const body = await readJson<{ device_not_required?: boolean }>(request);
      if (body?.device_not_required === undefined) {
        return;
      }

      const employeeId = String(params.employeeId);
      await delay();
      if (employeeId === EXEMPT_FAILS_EMPLOYEE_ID) {
        return serverError();
      }
      if (body.device_not_required) {
        exemptMockEmployee(employeeId);
      }
      return new HttpResponse(null, { status: 200 });
    }
  ),

  // Used by both the row menu and the modal's Send button
  http.post(
    '*/v1/organizations/:organizationId/desktop_app/mdm_agent_install_instructions',
    async ({ request }) => {
      const { employee_ids } = (await request.json()) as {
        employee_ids: string[];
      };
      await delay(400);
      const isOnlySendFailsRecipient =
        employee_ids.length === 1 && employee_ids[0] === SEND_FAILS_EMPLOYEE_ID;
      if (getMdmMockScenario() === 'sendError' || isOnlySendFailsRecipient) {
        return serverError();
      }
      return new HttpResponse(null, { status: 200 });
    }
  ),
];
